import { Popup } from 'maplibre-gl';
import { classLabel } from './poiCategories.js';
import { osmRef, osmUrl, fetchTags, fetchWikipedia, fetchGermanTitle, fetchArea } from '../data/details.js';

/** Layer, die auf Klick Auskunft geben – Reihenfolge = Priorität */
const QUERY_LAYERS = [
  'poi-symbol',
  'label-place-city', 'label-place-town', 'label-place-small',
  'label-peak',
  'building-fill',
  'water-fill',
  'road-motorway', 'road-trunk', 'road-primary', 'road-secondary',
  'road-tertiary', 'road-minor', 'road-service', 'road-track', 'road-path',
  'lc-forest', 'lc-park'
];

const KIND = {
  'poi-symbol': 'Punkt von Interesse',
  'label-place-city': 'Stadt',
  'label-place-town': 'Ortschaft',
  'label-place-small': 'Ortsteil',
  'label-peak': 'Gipfel',
  'building-fill': 'Gebäude',
  'water-fill': 'Gewässer',
  'lc-forest': 'Wald',
  'lc-park': 'Park'
};

const nameOf = (p = {}) => p['name:de'] || p.name_de || p.name || '';


/* ------------------------------------------------------- Einzelheiten aus OSM */

const PLACE_LAYERS = new Set(['label-place-city', 'label-place-town', 'label-place-small']);

/** Wochentage im OSM-Kürzel -> deutsch. Die Syntax bleibt sonst wie sie ist. */
const DAYS = { Mo: 'Mo', Tu: 'Di', We: 'Mi', Th: 'Do', Fr: 'Fr', Sa: 'Sa', Su: 'So', PH: 'Feiertag' };
const germanDays = (s) => s.replace(/\b(Mo|Tu|We|Th|Fr|Sa|Su|PH)\b/g, (d) => DAYS[d]);

const zahl = (n, stellen = 0) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: stellen }).format(n);

function row(host, label, value, href) {
  if (!value) return;
  const line = document.createElement('div');
  line.className = 'pop__row';
  const k = document.createElement('span');
  k.className = 'pop__key';
  k.textContent = label;
  const v = href ? document.createElement('a') : document.createElement('span');
  v.className = 'pop__val';
  v.textContent = value;
  if (href) { v.href = href; v.target = '_blank'; v.rel = 'noopener'; }
  line.append(k, v);
  host.append(line);
}

/** Öffnungszeiten lesbar machen: je Regel eine Zeile. */
function openingHours(host, raw) {
  if (!raw) return;
  const block = document.createElement('div');
  block.className = 'pop__hours';
  const head = document.createElement('span');
  head.className = 'pop__key';
  head.textContent = 'Öffnungszeiten';
  block.append(head);
  for (const rule of String(raw).split(';')) {
    const t = germanDays(rule.trim());
    if (!t) continue;
    const line = document.createElement('span');
    line.className = 'pop__hours-line';
    line.textContent = t;
    block.append(line);
  }
  host.append(block);
}

/** Merkmale, die oben schon als eigene Zeile erscheinen - unten nicht nochmal. */
const SCHON_GEZEIGT = new Set([
  'name', 'addr:street', 'addr:housenumber', 'addr:postcode', 'addr:city', 'addr:country',
  'opening_hours', 'phone', 'contact:phone', 'email', 'contact:email',
  'website', 'contact:website', 'url', 'operator', 'brand', 'cuisine', 'wheelchair',
  'ele', 'population', 'population:date', 'wikipedia', 'wikidata', 'source'
]);

/**
 * Der Rest, einklappbar. OSM kennt tausende Merkmale; eine feste Auswahl
 * verschweigt immer etwas. Oben steht das Übliche lesbar aufbereitet, hier
 * alles Weitere im Originalwortlaut.
 */
function alleMerkmale(host, t) {
  const rest = Object.entries(t)
    .filter(([k]) => !SCHON_GEZEIGT.has(k) && !k.startsWith('name:'))
    .sort(([a], [b]) => a.localeCompare(b));
  if (!rest.length) return;
  const box = document.createElement('details');
  box.className = 'pop__more';
  const sum = document.createElement('summary');
  sum.textContent = `Alle Merkmale (${rest.length})`;
  box.append(sum);
  for (const [k, v] of rest) {
    const line = document.createElement('div');
    line.className = 'pop__more-row';
    line.textContent = `${k} = ${v}`;
    box.append(line);
  }
  host.append(box);
}

/** Artikelauszug - bei Orten wie bei Bauwerken, die einen Eintrag haben. */
async function wikiBlock(host, t) {
  let tag = t['wikipedia:de'] || t.wikipedia;
  // Steht dort eine andere Sprache, über Wikidata die deutsche Fassung suchen.
  if (t.wikidata && !/^de:/.test(tag || '')) {
    tag = (await fetchGermanTitle(t.wikidata)) || tag;
  }
  const artikel = await fetchWikipedia(tag);
  if (!artikel?.text) return;
  const p = document.createElement('p');
  p.className = 'pop__text';
  p.textContent = artikel.text;
  host.append(p);
  row(host, '', 'Wikipedia-Artikel', artikel.url);
}

async function poiDetails(host, t) {
  const strasse = [t['addr:street'], t['addr:housenumber']].filter(Boolean).join(' ');
  const ort = [t['addr:postcode'], t['addr:city']].filter(Boolean).join(' ');
  row(host, 'Adresse', [strasse, ort].filter(Boolean).join(', '));
  openingHours(host, t.opening_hours);

  const tel = t.phone || t['contact:phone'];
  if (tel) row(host, 'Telefon', tel, `tel:${tel.replace(/[^+0-9]/g, '')}`);
  const mail = t.email || t['contact:email'];
  if (mail) row(host, 'E-Mail', mail, `mailto:${mail}`);
  const web = t.website || t['contact:website'] || t.url;
  if (web) {
    const schoen = web.replace(/^https?:\/\//, '').replace(/\/$/, '');
    row(host, 'Website', schoen.length > 38 ? `${schoen.slice(0, 38)}…` : schoen,
        /^https?:/.test(web) ? web : `https://${web}`);
  }
  row(host, 'Betreiber', t.operator || t.brand);
  row(host, 'Küche', t.cuisine && germanDays(t.cuisine).replace(/_/g, ' ').replace(/;/g, ', '));
  if (t.wheelchair) {
    row(host, 'Rollstuhl',
        { yes: 'ja', no: 'nein', limited: 'eingeschränkt' }[t.wheelchair] || t.wheelchair);
  }
  if (t.ele) row(host, 'Höhe', `${t.ele} m ü. M.`);
  await wikiBlock(host, t);
}

async function placeDetails(host, t) {
  if (t.population) {
    const datum = t['population:date'];
    const n = Number(t.population);
    row(host, 'Einwohner',
        Number.isFinite(n)
          ? `${zahl(n)}${datum ? ` (Stand ${datum})` : ''}`
          : t.population);
  }
  if (t.ele) row(host, 'Höhe', `${t.ele} m ü. M.`);
  if (t.website) {
    row(host, 'Website', t.website.replace(/^https?:\/\//, '').replace(/\/$/, ''), t.website);
  }

  const flaeche = await fetchArea(t.wikidata);
  if (flaeche) {
    row(host, 'Fläche', `${zahl(flaeche, 2)} km²`);
    const n = Number(t.population);
    if (Number.isFinite(n) && flaeche > 0) {
      row(host, 'Dichte', `${zahl(n / flaeche)} Einw./km²`);
    }
  }
  await wikiBlock(host, t);
}

/**
 * Nachladen stört den ersten Eindruck nicht: das Popup steht schon, hier
 * kommen die Einzelheiten hinterher. Schlägt der Abruf fehl, bleibt es bei
 * dem, was die Kachel wusste.
 */
async function loadDetails(host, feature, layerId) {
  const ref = osmRef(feature?.id);
  if (!ref) return;
  host.textContent = 'lädt …';
  const t = await fetchTags(ref);
  host.textContent = '';
  if (!t) return;

  if (PLACE_LAYERS.has(layerId)) await placeDetails(host, t);
  else await poiDetails(host, t);
  alleMerkmale(host, t);

  const link = document.createElement('a');
  link.className = 'pop__osm';
  link.href = osmUrl(ref);
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'In OpenStreetMap ansehen';
  host.append(link);
}

export function createInfoPopup({ map, onFavorite, onRoutePoint }) {
  const popup = new Popup({ closeButton: true, closeOnClick: true, maxWidth: '280px', offset: 12 });

  function openAt(lngLat, feature, layerId) {
    const p = feature?.properties || {};
    const name = nameOf(p);
    const kind = KIND[layerId] || (layerId.startsWith('road-') ? 'Strasse / Weg' : 'Objekt');
    // Bei Orten ist die Klasse ("city") nur die englische Fassung der Art -
    // zweimal dasselbe in der Unterzeile hilft niemandem.
    const isPlace = PLACE_LAYERS.has(layerId);
    const sub = !isPlace && p.class ? classLabel(p.class) : kind;

    const root = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'pop__title';
    title.textContent = name || sub;
    const subtitle = document.createElement('p');
    subtitle.className = 'pop__sub';
    subtitle.textContent = !name || isPlace || sub === kind ? kind : `${sub} · ${kind}`;
    root.append(title, subtitle);

    const tags = document.createElement('div');
    tags.className = 'pop__tags';
    const rows = [];
    if (p.subclass && p.subclass !== p.class) rows.push(['Art', p.subclass.replace(/_/g, ' ')]);
    if (p.ele) rows.push(['Höhe', `${p.ele} m ü. M.`]);
    if (p.housenumber) rows.push(['Nr.', p.housenumber]);
    if (p.ref) rows.push(['Nummer', p.ref]);
    rows.push(['Koordinaten', `${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)}`]);
    for (const [k, v] of rows) {
      const s = document.createElement('span');
      s.textContent = `${k}: ${v}`;
      tags.append(s);
    }
    root.append(tags);

    const details = document.createElement('div');
    details.className = 'pop__details';
    root.append(details);
    loadDetails(details, feature, layerId);

    const actions = document.createElement('div');
    actions.className = 'pop__actions';

    const favBtn = document.createElement('button');
    favBtn.type = 'button';
    favBtn.className = 'pop__btn pop__btn--fav';
    favBtn.textContent = 'Favorit';
    favBtn.addEventListener('click', async () => {
      favBtn.disabled = true;
      await onFavorite({
        name: name || sub,
        lat: lngLat.lat,
        lng: lngLat.lng,
        zoom: Math.round(map.getZoom()),
        kind: sub
      });
      popup.remove();
    });

    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.className = 'pop__btn';
    copyBtn.textContent = 'Koordinaten';
    copyBtn.addEventListener('click', async () => {
      await navigator.clipboard?.writeText(`${lngLat.lat.toFixed(6)}, ${lngLat.lng.toFixed(6)}`);
      copyBtn.textContent = 'Kopiert';
    });

    const startBtn = document.createElement('button');
    startBtn.type = 'button';
    startBtn.className = 'pop__btn';
    startBtn.textContent = 'Start';
    startBtn.addEventListener('click', () => {
      onRoutePoint?.('from', { lat: lngLat.lat, lng: lngLat.lng, name: name || sub });
      popup.remove();
    });

    const zielBtn = document.createElement('button');
    zielBtn.type = 'button';
    zielBtn.className = 'pop__btn';
    zielBtn.textContent = 'Ziel';
    zielBtn.addEventListener('click', () => {
      onRoutePoint?.('to', { lat: lngLat.lat, lng: lngLat.lng, name: name || sub });
      popup.remove();
    });

    actions.append(favBtn, startBtn, zielBtn, copyBtn);
    root.append(actions);

    popup.setLngLat(lngLat).setDOMContent(root).addTo(map);
  }

  map.on('click', (e) => {
    const layers = QUERY_LAYERS.filter((l) => map.getLayer(l));
    const hits = map.queryRenderedFeatures(
      [[e.point.x - 6, e.point.y - 6], [e.point.x + 6, e.point.y + 6]],
      { layers }
    );
    if (!hits.length) {
      openAt(e.lngLat, null, 'point');
      return;
    }
    const best = hits.reduce((a, b) =>
      QUERY_LAYERS.indexOf(a.layer.id) <= QUERY_LAYERS.indexOf(b.layer.id) ? a : b
    );
    openAt(e.lngLat, best, best.layer.id);
  });

  map.on('mouseenter', 'poi-symbol', () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', 'poi-symbol', () => { map.getCanvas().style.cursor = ''; });

  return popup;
}
